import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Clock, CheckCircle2 } from "lucide-react";

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [status, setStatus] = useState("checking"); // checking, valid, invalid, done
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setError("Missing token");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/validate-token", { params: { token } });
        setInfo(data);
        setStatus("valid");
      } catch (e) {
        setStatus("invalid");
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/set-password", { token, password });
      setStatus("done");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
           style={{ background: "linear-gradient(180deg, #0b2c47 0%, #145c8c 100%)" }}>
        <div className="absolute inset-0 opacity-30"
             style={{
               backgroundImage: "radial-gradient(circle at 20% 30%, #00b5cc 0%, transparent 40%), radial-gradient(circle at 80% 70%, #145c8c 0%, transparent 50%)"
             }} />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <Clock className="h-8 w-8 text-white" strokeWidth={1.5} />
          <div>
            <h1 className="text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: "Manrope" }}>
              {info?.purpose === "reset" ? "Reset your password" : "Set your password"}
            </h1>
            <p className="mt-4 text-slate-200 max-w-md">
              {info?.purpose === "reset"
                ? "Choose a new password to regain access to your workspace."
                : "Welcome aboard — pick a password to activate your account."}
            </p>
          </div>
          <div className="text-xs text-slate-300">© Dreamtime Resorts</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-md">
          {status === "checking" && (
            <div className="text-sm text-slate-400" data-testid="set-password-loading">Verifying link…</div>
          )}

          {status === "invalid" && (
            <div data-testid="set-password-invalid">
              <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Link invalid</h2>
              <p className="mt-3 text-sm text-slate-600">{error || "This link is no longer valid."}</p>
              <Link to="/login" className="inline-block mt-6 text-sm text-[#145c8c] hover:underline">← Back to sign in</Link>
            </div>
          )}

          {status === "valid" && (
            <>
              <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>
                {info?.purpose === "reset" ? "Reset password" : "Create your password"}
              </h2>
              <p className="mt-2 text-sm text-slate-500">Setting password for <span className="font-medium text-slate-700">{info?.email}</span></p>

              <form className="mt-8 space-y-5" onSubmit={submit} data-testid="set-password-form">
                <div>
                  <Label htmlFor="pw">New password</Label>
                  <PasswordInput id="pw" value={password} autoComplete="new-password"
                         onChange={(e) => setPassword(e.target.value)} required minLength={8}
                         className="mt-1" data-testid="set-password-input"
                         toggleTestId="set-password-toggle" />
                </div>
                <div>
                  <Label htmlFor="pw2">Confirm password</Label>
                  <PasswordInput id="pw2" value={confirm} autoComplete="new-password"
                         onChange={(e) => setConfirm(e.target.value)} required minLength={8}
                         className="mt-1" data-testid="set-password-confirm"
                         toggleTestId="set-password-confirm-toggle" />
                </div>
                {error && (
                  <div className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-700 border border-red-200"
                       data-testid="set-password-error">{error}</div>
                )}
                <Button type="submit" disabled={submitting} className="w-full h-11 text-white"
                        style={{ background: "#145c8c" }}
                        data-testid="set-password-submit">
                  {submitting ? "Saving…" : (info?.purpose === "reset" ? "Reset password" : "Set password & sign in")}
                </Button>
              </form>
            </>
          )}

          {status === "done" && (
            <div className="text-center py-10" data-testid="set-password-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h2 className="mt-4 text-2xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>
                Password saved
              </h2>
              <p className="mt-2 text-sm text-slate-500">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
